/** Docs (UA): see modules/game/DOCS.md — success-screen invite banner.
 *  Decision 2 (21.08): appears right after purchase and stays while the
 *  success screen is open — the person takes the coffee first, then plays.
 *  Decision 24.08: the banner speaks the PRODUCT's language (it lives on the
 *  payment success screen); the emerald game world starts only when the
 *  game opens full-screen. */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '../../../../shared/hooks/useAppStore';
import { EVENT_NAMES, useSendEventLog } from '../../../event-log';
import { useLoyaltyStore } from '../../../loyalty/hooks/useLoyaltyStore';
import { accrueGameReward } from '../../api/game.api';
import { themeForMachineType } from '../../game.constants';
import { useGameTry } from '../../hooks/useGameTry';
import { GameScreen } from '../GameScreen/GameScreen';
import type { GameCloseReason } from '../GameScreen/GameScreen';
import { GiftBoxIcon } from '../icons';
import styles from './GameBanner.module.css';

/** The game events carry no number: `timeout_value` means «seconds left on the
 *  timer» everywhere else, and the score is not that. Passing an explicit 0
 *  keeps the ambient timer value out of the column (SWRN-1898). */
const NO_VALUE = 0;

const GameBanner = () => {
    const { t } = useTranslation();
    const order = useAppStore((s) => s.order);
    const vm = useAppStore((s) => s.vm);
    const loyaltyId = useLoyaltyStore((s) => s.loyalty_id);
    const isLoyaltyProgramActive = useLoyaltyStore((s) => s.isLoyaltyProgramActive);
    const isGameEnabled = useLoyaltyStore((s) => s.isGameEnabled);
    const setCurrentBalance = useLoyaltyStore((s) => s.setCurrentBalance);
    const [isGameOpen, setGameOpen] = useState(false);
    const [rewardSent, setRewardSent] = useState(false);
    const { sendEvent } = useSendEventLog();

    const gameTry = useGameTry(order.id ?? 'unknown');

    // The game is a loyalty feature: no active program — no banner.
    if (!isLoyaltyProgramActive() || !order.id) return null;
    // Shown only where it was switched on (Vadym, 25.08). The flag rides in
    // `loyalty_config`; until that field ships it reads as absent, so the game
    // stays off in production and on for the local stand.
    if (!isGameEnabled) return null;
    if (!gameTry.isTryAvailable && !isGameOpen) return null;

    const handleBannerClick = (): void => {
        sendEvent({ name: EVENT_NAMES.static.GAME_BANNER_CLICKED, timeout_value: NO_VALUE });
        setGameOpen(true);
    };

    const handleGameEnd = (won: boolean): void => {
        sendEvent({
            name: won
                ? EVENT_NAMES.static.GAME_FINISHED_WIN
                : EVENT_NAMES.static.GAME_FINISHED_LOSS,
            timeout_value: NO_VALUE,
        });
    };

    const handleClose = (reason: GameCloseReason): void => {
        // «Not now» on the start screen is not a return from the game — only
        // the button after a finished game is what Vadym asked to measure.
        if (reason === 'done') {
            sendEvent({
                name: EVENT_NAMES.static.GAME_RETURNED_TO_SUCCESS,
                timeout_value: NO_VALUE,
            });
        }
        setGameOpen(false);
    };

    const handleThreshold = (): void => {
        // The point locks in the moment the bar reaches the gift; losing the
        // remaining lives never takes it back (design frame).
        if (rewardSent) return;
        setRewardSent(true);
        // `order.user_id` — the wallet user, the same id the balance request and
        // web0's purchase accrual send. NOT `loyalty_user_id`: that is the PK of
        // the `loyalty_user` row, a different key space, and write-on looks the
        // user up by `loyalty_user.user_id`.
        void accrueGameReward({
            orderId: Number(order.id),
            loyaltyId,
            userId: Number(order.user_id),
        }).then((reward) => {
            // `new_balance` from the server, not an optimistic +1: the progress
            // banner keeps rendering behind the game and nothing refetches it
            // when the game closes, so this is what the person comes back to.
            if (reward) setCurrentBalance(reward.new_balance);
        });
    };

    return (
        <>
            {!isGameOpen && (
                <button type="button" className={styles.banner} onClick={handleBannerClick}>
                    <span className={styles.iconCircle}>
                        <GiftBoxIcon size={26} />
                    </span>
                    <span className={styles.textBox}>
                        <span className={styles.title}>{t('Game.Banner.Title')}</span>
                        <span className={styles.subtitle}>{t('Game.Banner.Subtitle')}</span>
                    </span>
                    <span className={styles.playPill}>▶ {t('Game.Play')}</span>
                </button>
            )}
            {isGameOpen && (
                <GameScreen
                    theme={themeForMachineType(vm.type_id)}
                    record={gameTry.record}
                    soundEnabled={gameTry.soundEnabled}
                    onSoundToggle={gameTry.setSoundEnabled}
                    onTryStart={gameTry.markTryUsed}
                    onThreshold={handleThreshold}
                    onFinish={gameTry.saveRecord}
                    onGameEnd={handleGameEnd}
                    onClose={handleClose}
                />
            )}
        </>
    );
};

export { GameBanner };
